import { Button, createStyles, Loader } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from '@mantine/form'
import { DateRangePicker } from '@mantine/dates'
import 'dayjs/locale/nb'
import { Calendar, Check, History, X } from 'tabler-icons-react'
import {
	getAdmissionPeriod,
	putAdmissionPeriod,
} from '../services/Applications'
import { IAdmissionPeriod } from '../types/types'
import dayjs from 'dayjs'
import { showNotification, updateNotification } from '@mantine/notifications'
import { getUserCommittees, IRoleInCommittee } from '../services/Committees'

const useStyles = createStyles((theme) => ({
	pageWrapper: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		margin: 'auto',
		width: '400px',
		color: 'white',
		'@media (max-width: 400px)': {
			fontSize: 'small',
			width: '90%',
		},
	},
	header: {
		textAlign: 'center',
		h1: {
			fontWeight: 'lighter',
			margin: '1rem 0 0 0',
		},
		p: {
			margin: '10px 0 0 0',
		},
	},
	admissionPeriodStatusText: {
		fontWeight: 'lighter',
		b: {
			color: theme.colors.ntnui_yellow[9],
		},
	},
	loaderWrapper: {
		alignSelf: 'center',
	},
	dateRangeInput: {
		backgroundColor: 'transparent',
		color: 'white',
		border: '2px solid ' + theme.colors.ntnui_yellow[9],
	},
	dateRangeError: {
		margin: 0,
		position: 'absolute',
	},
	dateRangeLabel: {
		textAlign: 'left',
		color: 'white',
	},
	dateRangeIcon: {
		color: theme.colors.ntnui_yellow[9],
	},
	unchangedText: {
		color: theme.colors.ntnui_yellow[9],
		fontSize: '0.8rem',
	},
	dateRangeRightSection: {
		button: {
			color: theme.colors.ntnui_yellow[9],
		},
	},
	buttonWrapper: {
		margin: '30px 0 5px 0',
		width: '100%',
		display: 'flex',
		gap: '10px',
	},
	cancelButton: {
		width: '100%',
		backgroundColor: theme.colors.ntnui_blue[9],
		transition: '0.3s',
		border: '2px solid' + theme.colors.ntnui_blue[9],
		':hover': {
			border: '2px solid' + theme.colors.ntnui_blue[9],
			color: theme.colors.ntnui_blue[9],
			backgroundColor: 'transparent',
		},
	},
	confirmButton: {
		width: '100%',
		backgroundColor: theme.colors.ntnui_green[9],
		transition: '0.3s',
		border: '2px solid' + theme.colors.ntnui_green[9],
		':hover': {
			border: '2px solid' + theme.colors.ntnui_green[9],
			color: theme.colors.ntnui_green[9],
			backgroundColor: 'transparent',
		},
	},
}))

interface AdmissionPeriodProps {
	isSL?: boolean
}

interface LocationState {
	isOrganizer?: boolean
}

function AdmissionPeriod({ isSL = false }: AdmissionPeriodProps) {
	const { classes } = useStyles()
	const navigate = useNavigate()
	const location = useLocation()

	const [isLoading, setIsLoading] = useState(false)
	const [isPeriodSet, setIsPeriodSet] = useState(false)

	const today = dayjs().startOf('day')

	const [previousDates, setPreviousDates] = useState<Date[]>([
		today.toDate(),
		today.add(7, 'day').toDate(),
	])

	const [differentFromDb, setChanged] = useState(false)
	const [hasError, setHasError] = useState(false)
	const [initialChange, setHasInitialChange] = useState(false)
	const [setBy, setSetBy] = useState('')
	const [updatedDateValue, setUpdatedDateValue] = useState<Date>()

	const form = useForm({
		initialValues: {
			dateRangeInput: [previousDates[0], previousDates[1]],
		},
		validate: {
			dateRangeInput: (value) =>
				!value[0] || !value[1] ? 'Du må velge to datoer' : null,
		},
	})

	useEffect(() => {
		async function loadAdmissionPeriod() {
			setIsLoading(true)

			try {
				if (isSL) {
					/*
					 * For Studentlekene, check whether the user belongs
					 * to the Studentlekene board.
					 */
					const userCommittees = await getUserCommittees()

					const isOrganizer = userCommittees.some(
						(roleInCommittee: IRoleInCommittee) =>
							roleInCommittee.committee.slug === 'studentlekene'
					)

					if (!isOrganizer) {
						navigate('/dashboard')
						return
					}
				} else {
					/*
					 * Keep the existing organizer check for the normal
					 * admission period.
					 */
					const locationState = location.state as LocationState | null

					if (!locationState?.isOrganizer) {
						navigate('/dashboard')
						return
					}
				}

				const response = await getAdmissionPeriod(isSL)

				const retrievedPeriod: Date[] = [
					new Date(response.admissionPeriod.start_date),
					new Date(response.admissionPeriod.end_date),
				]

				setSetBy(response.admissionPeriod.set_by)
				setUpdatedDateValue(response.admissionPeriod.updated_date)
				form.setValues({
					dateRangeInput: retrievedPeriod,
				})
				setPreviousDates(retrievedPeriod)
				setIsPeriodSet(true)
				setHasInitialChange(false)
			} catch (error: any) {
				const status = error.response?.status

				if (status === 401) {
					navigate(isSL ? '/studentlekene/login' : '/login')
				} else if (status !== 404) {
					showNotification({
						loading: false,
						color: 'red',
						icon: <X size={18} />,
						title: 'En feil oppstod',
						message: 'Kunne ikke hente opptaksperioden',
						autoClose: false,
					})
				}
			} finally {
				setIsLoading(false)
			}
		}

		loadAdmissionPeriod()
	}, [isSL, location.state, navigate])

	const saveAdmissionPeriod = async () => {
		if (form.validate().hasErrors) {
			return
		}

		const start = form.values.dateRangeInput[0]
		const end = form.values.dateRangeInput[1]

		const admissionPeriod: IAdmissionPeriod = {
			start_date: dayjs(start).format('YYYY-MM-DD'),
			end_date: dayjs(end).format('YYYY-MM-DD'),

			/*
			 * false creates or updates the normal period.
			 * true creates or updates the SL period.
			 */
			sl: isSL,
		}

		showNotification({
			id: 'admission-period-notification',
			loading: true,
			color: 'green',
			icon: <Check size={18} />,
			title: 'Oppdaterer opptaksperiode',
			message: '',
			autoClose: false,
		})

		try {
			const response = await putAdmissionPeriod(admissionPeriod)

			setChanged(false)
			setIsPeriodSet(true)
			setPreviousDates([start, end])
			setSetBy(response.admissionPeriod.set_by)
			setUpdatedDateValue(response.admissionPeriod.updated_date)

			updateNotification({
				id: 'admission-period-notification',
				loading: false,
				color: 'green',
				icon: <Check size={18} />,
				title: `Opptaksperiode ${isPeriodSet ? 'oppdatert' : 'satt'}!`,
				message: '',
				autoClose: 7000,
			})
		} catch (error: any) {
			const forbidden = error.response?.status === 403

			updateNotification({
				id: 'admission-period-notification',
				loading: false,
				color: 'red',
				icon: <X size={18} />,
				title: forbidden
					? 'Du har ikke tilgang til å endre opptaksperioden!'
					: 'En feil oppstod!',
				message: forbidden
					? isSL
						? 'Du må være i Studentlekene-styret for å kunne endre opptaksperioden'
						: 'Du må være i Hovedstyret for å kunne endre opptaksperioden'
					: 'Kunne ikke oppdatere opptaksperioden',
				autoClose: false,
			})
		}
	}

	const undoChanges = () => {
		form.setValues({
			dateRangeInput: [previousDates[0], previousDates[1]],
		})
	}

	useEffect(() => {
		setHasInitialChange(true)
	}, [form.values.dateRangeInput])

	useEffect(() => {
		const datesAreUnchanged =
			dayjs(form.values.dateRangeInput[0]).isSame(previousDates[0]) &&
			dayjs(form.values.dateRangeInput[1]).isSame(previousDates[1])

		setChanged(!datesAreUnchanged)
		setHasError(form.validate().hasErrors)
	}, [form.values.dateRangeInput, previousDates])

	const admissionPeriodStatusText = isPeriodSet ? (
		<>
			Lagret opptaksperiode <br />
			<b>
				{previousDates[0]?.toLocaleDateString('no-NO', {
					month: '2-digit',
					day: '2-digit',
					year: '2-digit',
				})}
				{' - '}
				{previousDates[1]?.toLocaleDateString('no-NO', {
					month: '2-digit',
					day: '2-digit',
					year: '2-digit',
				})}
			</b>
		</>
	) : (
		<>Det er ikke satt noen opptaksperiode enda.</>
	)

	return (
		<div className={classes.pageWrapper}>
			<div className={classes.header}>
				<h1>{isSL ? 'Opptaksperiode for Studentlekene' : 'Opptaksperiode'}</h1>

				<p>
					Opptaksperioden bestemmer når studenter har mulighet til å sende inn
					søknad.
				</p>

				<h3 className={classes.admissionPeriodStatusText}>
					{admissionPeriodStatusText}
				</h3>
			</div>

			{isLoading ? (
				<div className={classes.loaderWrapper}>
					<Loader color='yellow' variant='dots' />
				</div>
			) : (
				<DateRangePicker
					locale='nb'
					amountOfMonths={1}
					icon={<Calendar />}
					closeCalendarOnChange={false}
					classNames={{
						input: classes.dateRangeInput,
						label: classes.dateRangeLabel,
						icon: classes.dateRangeIcon,
						error: classes.dateRangeError,
						rightSection: classes.dateRangeRightSection,
					}}
					label='Opptaksperiode'
					placeholder='Velg en tidsperiode'
					description={
						setBy &&
						updatedDateValue &&
						`Satt av ${setBy} ${dayjs(updatedDateValue)
							.locale('nb')
							.format('D. MMM HH:mm')}`
					}
					{...form.getInputProps('dateRangeInput')}
					onBlur={() => form.validateField('dateRangeInput')}
				/>
			)}

			{isPeriodSet && !differentFromDb && initialChange && (
				<i className={classes.unchangedText}>Endre opptaksperioden for å lagre</i>
			)}

			<div className={classes.buttonWrapper}>
				<Button
					disabled={isPeriodSet && !differentFromDb}
					className={classes.cancelButton}
					leftIcon={<History />}
					onClick={undoChanges}
				>
					Fjern endringer
				</Button>

				<Button
					className={classes.confirmButton}
					disabled={(isPeriodSet && !differentFromDb) || hasError}
					leftIcon={<Check />}
					onClick={saveAdmissionPeriod}
				>
					{isPeriodSet ? 'Oppdater' : 'Lagre'}
				</Button>
			</div>
		</div>
	)
}

export default AdmissionPeriod
